import {
    Badge,
    Box,
    Button,
    Checkbox,
    Divider,
    IconButton,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Popover,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import FilterListIcon from "@mui/icons-material/FilterList";
import React, { useEffect, useMemo, useState } from "react";
import { FilterOption } from "./availsPlusFilterConfig";

interface EnumColumnFilterProps {
    label: string;
    options: FilterOption[];
    selectedValues: string[] | undefined;
    onApply: (values: string[] | null) => void;
}

function valuesEqual(a: string[], b: string[]): boolean {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((value, index) => value === sortedB[index]);
}

export default function EnumColumnFilter({
    label,
    options,
    selectedValues,
    onApply,
}: EnumColumnFilterProps) {
    const allValues = useMemo(
        () => options.map((option) => option.value),
        [options]
    );
    const isActive =
        selectedValues != null && selectedValues.length < options.length;

    const activeSummary = useMemo(() => {
        if (!isActive || !selectedValues?.length) return null;
        return selectedValues
            .map(
                (value) =>
                    options.find((option) => option.value === value)?.label ??
                    value
            )
            .join(", ");
    }, [isActive, options, selectedValues]);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [search, setSearch] = useState("");
    const [draft, setDraft] = useState<string[]>(allValues);

    useEffect(() => {
        if (!anchorEl) return;
        setDraft(selectedValues ?? allValues);
        setSearch("");
    }, [anchorEl, allValues, selectedValues]);

    const filteredOptions = useMemo(() => {
        const query = search.trim().toLowerCase();
        if (!query) return options;
        return options.filter((option) =>
            option.label.toLowerCase().includes(query)
        );
    }, [options, search]);

    const allDraftSelected = draft.length === allValues.length;
    const someDraftSelected = draft.length > 0 && !allDraftSelected;

    const toggleValue = (value: string) => {
        setDraft((prev) =>
            prev.includes(value)
                ? prev.filter((item) => item !== value)
                : [...prev, value]
        );
    };

    const handleSelectAll = () => {
        setDraft(allDraftSelected ? [] : allValues);
    };

    const handleApply = () => {
        if (!draft.length) return;
        if (draft.length === allValues.length) {
            onApply(null);
        } else {
            onApply(draft);
        }
        setAnchorEl(null);
    };

    const handleClear = () => {
        onApply(null);
        setAnchorEl(null);
    };

    const filterButton = (
        <IconButton
            size="small"
            aria-label={
                isActive
                    ? `${label} filtered: ${activeSummary}`
                    : `Filter ${label}`
            }
            onClick={(event) => {
                event.stopPropagation();
                setAnchorEl(event.currentTarget);
            }}
            sx={{
                p: 0.35,
                ...(isActive
                    ? {
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                          "&:hover": {
                              bgcolor: "primary.dark",
                          },
                      }
                    : {
                          color: "text.secondary",
                          "&:hover": {
                              bgcolor: "action.hover",
                              color: "text.primary",
                          },
                      }),
            }}
        >
            {isActive ? (
                <FilterAltIcon fontSize="small" />
            ) : (
                <FilterListIcon fontSize="small" />
            )}
        </IconButton>
    );

    return (
        <>
            <Tooltip
                title={
                    isActive
                        ? `${label}: ${activeSummary}`
                        : `Filter ${label}`
                }
                arrow
                placement="top"
            >
                {isActive ? (
                    <Badge
                        badgeContent={selectedValues?.length ?? 0}
                        color="secondary"
                        overlap="circular"
                        sx={{
                            "& .MuiBadge-badge": {
                                fontSize: "0.65rem",
                                height: 16,
                                minWidth: 16,
                                fontWeight: 700,
                            },
                        }}
                    >
                        {filterButton}
                    </Badge>
                ) : (
                    filterButton
                )}
            </Tooltip>
            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={() => setAnchorEl(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                transformOrigin={{ vertical: "top", horizontal: "center" }}
                onClick={(event) => event.stopPropagation()}
            >
                <Box sx={{ width: 240, p: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                        Filter: {label}
                    </Typography>
                    {isActive && activeSummary && (
                        <Typography
                            variant="caption"
                            color="primary"
                            display="block"
                            sx={{ mb: 1 }}
                        >
                            Active: {activeSummary}
                        </Typography>
                    )}
                    <TextField
                        size="small"
                        fullWidth
                        placeholder="Search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        sx={{ mb: 1 }}
                    />
                    <List dense disablePadding sx={{ maxHeight: 220, overflowY: "auto" }}>
                        <ListItem disablePadding>
                            <ListItemButton onClick={handleSelectAll} dense>
                                <ListItemIcon sx={{ minWidth: 32 }}>
                                    <Checkbox
                                        edge="start"
                                        size="small"
                                        checked={allDraftSelected}
                                        indeterminate={someDraftSelected}
                                        tabIndex={-1}
                                        disableRipple
                                    />
                                </ListItemIcon>
                                <ListItemText primary="(Select All)" />
                            </ListItemButton>
                        </ListItem>
                        <Divider />
                        {filteredOptions.map((option) => (
                            <ListItem key={option.value || "(blank)"} disablePadding>
                                <ListItemButton
                                    onClick={() => toggleValue(option.value)}
                                    dense
                                >
                                    <ListItemIcon sx={{ minWidth: 32 }}>
                                        <Checkbox
                                            edge="start"
                                            size="small"
                                            checked={draft.includes(option.value)}
                                            tabIndex={-1}
                                            disableRipple
                                        />
                                    </ListItemIcon>
                                    <ListItemText primary={option.label} />
                                </ListItemButton>
                            </ListItem>
                        ))}
                    </List>
                    {!draft.length && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: "block" }}>
                            Select at least one value.
                        </Typography>
                    )}
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            mt: 1.5,
                        }}
                    >
                        <Button size="small" onClick={handleClear}>
                            Clear
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!draft.length}
                            onClick={handleApply}
                        >
                            Apply
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
}

export { valuesEqual };
